import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Table, Alert, Badge } from 'react-bootstrap';
import { FaArrowLeft, FaCalendarAlt, FaPlus, FaTrash, FaInfoCircle } from 'react-icons/fa';
import axios from 'axios';
import ModalConfirmacion from '../../../components/modals/ModalConfirmacion';
import "react-toastify/dist/ReactToastify.css";

const API_URL = process.env.REACT_APP_PYTHON_API_URL || "http://localhost:8003";

const ProgramacionMenuCiclico = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [menusCiclicos, setMenusCiclicos] = useState([]);
  const [programaciones, setProgramaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    menu_ciclico_cabecera_id: '',
    nombre: '',
    fecha_inicio: '',
    fecha_fin: '',
  });
  const [menuSeleccionado, setMenuSeleccionado] = useState(null);
  const [duracionCiclo, setDuracionCiclo] = useState(0);
  const [diasProgramacion, setDiasProgramacion] = useState(0);
  const [ciclosCompletos, setCiclosCompletos] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [programacionToDelete, setProgramacionToDelete] = useState(null);

  useEffect(() => {
    fetchMenusCiclicos();
    fetchProgramaciones();
  }, []);

  useEffect(() => {
    if (formData.fecha_inicio && formData.fecha_fin) {
      const inicio = new Date(formData.fecha_inicio);
      const fin = new Date(formData.fecha_fin);
      const dias = Math.floor((fin - inicio) / (1000 * 60 * 60 * 24)) + 1;
      setDiasProgramacion(dias);
      
      if (duracionCiclo > 0) {
        setCiclosCompletos(Math.floor(dias / duracionCiclo));
      }
    }
  }, [formData.fecha_inicio, formData.fecha_fin, duracionCiclo]);

  const fetchMenusCiclicos = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/mantenimientos/planificacion-insumos/menus-ciclicos/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const menusActivos = response.data.filter(m => m.activo);
      setMenusCiclicos(menusActivos);
    } catch (error) {
      console.error('Error obteniendo menús cíclicos:', error);
      toast.error('Error al obtener menús cíclicos');
    }
  };

  const fetchProgramaciones = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/procesos/planificacion-insumos/programacion-menu-ciclico/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setProgramaciones(response.data);
    } catch (error) {
      console.error('Error obteniendo programaciones:', error);
      toast.error('Error al obtener programaciones');
    } finally {
      setLoading(false);
    }
  };

  const obtenerDuracionMenu = async (menuId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/mantenimientos/planificacion-insumos/menus-ciclicos/${menuId}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (response.data.detalles && response.data.detalles.length > 0) {
        // Calcular duración: máximo semana_ordinal * 7 días
        const maxSemana = Math.max(...response.data.detalles.map(d => d.semana_ordinal || 1));
        const duracion = maxSemana * 7;
        setDuracionCiclo(duracion);
        return duracion;
      }
      return 0;
    } catch (error) {
      console.error('Error obteniendo duración del menú:', error);
      toast.error('Error al obtener información del menú cíclico');
      return 0;
    }
  };

  const handleMenuChange = async (e) => {
    const menuId = parseInt(e.target.value);
    setFormData({ ...formData, menu_ciclico_cabecera_id: menuId });
    
    if (menuId) {
      const menu = menusCiclicos.find(m => m.id === menuId);
      setMenuSeleccionado(menu);
      const duracion = await obtenerDuracionMenu(menuId);
      setDuracionCiclo(duracion);
    } else {
      setMenuSeleccionado(null);
      setDuracionCiclo(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.menu_ciclico_cabecera_id) {
      toast.error('Por favor seleccione un menú cíclico');
      return;
    }

    if (!formData.nombre.trim()) {
      toast.error('Por favor ingrese un nombre para la programación');
      return;
    }

    if (!formData.fecha_inicio || !formData.fecha_fin) {
      toast.error('Por favor seleccione las fechas de inicio y fin');
      return;
    }

    if (diasProgramacion < duracionCiclo) {
      toast.error(`El rango de días (${diasProgramacion}) debe ser al menos igual a la duración del menú cíclico (${duracionCiclo} días)`);
      return;
    }

    if (diasProgramacion % duracionCiclo !== 0) {
      toast.error(
        `El rango de días (${diasProgramacion}) debe ser múltiplo de la duración del menú cíclico (${duracionCiclo} días). ` +
        `Ejemplo: si el ciclo es de ${duracionCiclo} días, puedes programar ${duracionCiclo}, ${duracionCiclo * 2}, ${duracionCiclo * 3}, etc.`
      );
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}/api/procesos/planificacion-insumos/programacion-menu-ciclico/`,
        {
          menu_ciclico_cabecera_id: parseInt(formData.menu_ciclico_cabecera_id),
          nombre: formData.nombre,
          fecha_inicio: formData.fecha_inicio,
          fecha_fin: formData.fecha_fin,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success(response.data.message || 'Programación creada exitosamente');
      setFormData({
        menu_ciclico_cabecera_id: '',
        nombre: '',
        fecha_inicio: '',
        fecha_fin: '',
      });
      setMenuSeleccionado(null);
      setDuracionCiclo(0);
      fetchProgramaciones();
    } catch (error) {
      console.error('Error creando programación:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Error al crear la programación';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (programacion) => {
    setProgramacionToDelete(programacion);
    setShowConfirmModal(true);
  };

  const confirmDeleteProgramacion = async () => {
    if (!programacionToDelete) return;

    try {
      setLoading(true);
      await axios.delete(
        `${API_URL}/api/procesos/planificacion-insumos/programacion-menu-ciclico/${programacionToDelete.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Programación eliminada exitosamente');
      setShowConfirmModal(false);
      setProgramacionToDelete(null);
      // Forzar actualización de la lista
      await fetchProgramaciones();
    } catch (error) {
      console.error('Error eliminando programación:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Error al eliminar la programación';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-PE', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  return (
    <>
      {/* Digital Harmony Styles */}
      <style>
        {`
          .dh-reporte-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          
          .dh-main-title {
            font-size: 2.5rem;
            font-weight: 700;
            margin: 0;
          }
          
          .dh-main-subtitle {
            font-size: 1.1rem;
            opacity: 0.9;
            margin: 0.5rem 0 0 0;
          }
          
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          
          .dh-card-header {
            border-radius: 15px 15px 0 0;
            padding: 1rem 1.5rem;
            border: none;
            font-weight: 700;
          }
          
          .dh-card-header-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          
          .dh-card-header-success {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            color: white;
          }
          
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 8px 16px;
            font-weight: 600;
            font-size: 0.875rem;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          
          .btn-dh-danger {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 8px 16px;
            font-weight: 600;
            font-size: 0.875rem;
            box-shadow: 0 4px 14px rgba(239, 68, 68, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-danger:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
            color: white;
          }
          
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 8px 16px;
            font-weight: 600;
            font-size: 0.875rem;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #4b5563 0%, #374151 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }
          
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 8px 12px;
            font-size: 0.875rem;
            transition: all 0.3s ease;
            background: rgba(255, 255, 255, 0.9);
          }
          
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
            background: white;
            outline: none;
          }
          
          .dh-form-label {
            color: #374151;
            font-weight: 600;
            font-size: 0.875rem;
            margin-bottom: 8px;
          }
          
          .dh-table {
            border-collapse: separate;
            border-spacing: 0;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.05);
          }
          
          .dh-table thead th {
            background: #374151 !important;
            color: white !important;
            font-weight: 700 !important;
            font-size: 0.7rem !important;
            text-transform: uppercase !important;
            padding: 0.5rem 0.4rem !important;
            border: none !important;
            position: sticky !important;
            top: 0 !important;
            z-index: 10 !important;
          }
          
          .dh-table tbody td {
            padding: 0.5rem 0.4rem;
            border: none;
            border-bottom: 1px solid #f3f4f6;
            font-size: 0.75rem;
          }
          
          .dh-table tbody tr:hover {
            background: linear-gradient(135deg, #f9fafb 0%, rgba(102, 126, 234, 0.02) 100%);
          }
          
          .dh-alert {
            border: none;
            border-radius: 12px;
            padding: 1rem 1.25rem;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          }
          
          .dh-alert-info {
            background: linear-gradient(135deg, rgba(0, 198, 255, 0.1) 0%, rgba(0, 114, 255, 0.1) 100%);
            border-left: 4px solid #00c6ff;
            color: #0056d1;
          }
          
          .dh-alert-success {
            background: linear-gradient(135deg, rgba(17, 153, 142, 0.1) 0%, rgba(56, 239, 125, 0.1) 100%);
            border-left: 4px solid #11998e;
            color: #047857;
          }
          
          .dh-alert-warning {
            background: linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(251, 191, 36, 0.1) 100%);
            border-left: 4px solid #f59e0b;
            color: #92400e;
          }
        `}
      </style>

      <div className="dh-reporte-bg">
        <ToastContainer />

        <Container>
          <Row className="justify-content-center">
            <Col lg={12}>
              <Card className="dh-card mb-4">
                <Card.Header className="dh-card-header dh-card-header-primary">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <FaCalendarAlt className="me-3" size={20} />
                      <h5 className="mb-0" style={{ fontSize: '0.95rem', fontWeight: 700 }}>Programación de Menú Cíclico</h5>
                    </div>
                    <Button
                      className="btn-dh-secondary"
                      onClick={() => navigate(-1)}
                    >
                      <FaArrowLeft className="me-2" />
                      Volver
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body className="p-4">
                  <div className="mb-4">
                    <h6 className="mb-3" style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                      <FaPlus className="me-2" />
                      Crear Nueva Programación
                    </h6>
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label className="dh-form-label">Menú Cíclico *</Form.Label>
                      <Form.Select
                        value={formData.menu_ciclico_cabecera_id}
                        onChange={handleMenuChange}
                        className="dh-form-control"
                        required
                      >
                        <option value="">Seleccione un menú cíclico</option>
                        {menusCiclicos.map((menu) => (
                          <option key={menu.id} value={menu.id}>
                            {menu.nombre}
                          </option>
                        ))}
                      </Form.Select>
                      {menuSeleccionado && (
                        <Form.Text className="text-muted" style={{ fontSize: '0.8rem' }}>
                          Menú seleccionado: {menuSeleccionado.nombre}
                        </Form.Text>
                      )}
                    </Form.Group>

                    {duracionCiclo > 0 && (
                      <Alert variant="info" className="dh-alert dh-alert-info mb-3" style={{ fontSize: '0.875rem' }}>
                        <FaInfoCircle /> <strong>Duración del menú cíclico:</strong> {duracionCiclo} días
                        <br />
                        <small style={{ fontSize: '0.8rem' }}>El rango de fechas debe ser múltiplo de {duracionCiclo} días</small>
                      </Alert>
                    )}

                    <Form.Group className="mb-3">
                      <Form.Label className="dh-form-label">Nombre de la Programación *</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.nombre}
                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                        placeholder="Ej: Campamento Verano 2025 - Semana 1 a 4"
                        className="dh-form-control"
                        required
                      />
                    </Form.Group>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="dh-form-label">
                            <FaCalendarAlt className="me-2" />
                            Fecha Inicio *
                          </Form.Label>
                          <Form.Control
                            type="date"
                            value={formData.fecha_inicio}
                            onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                            className="dh-form-control"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="dh-form-label">
                            <FaCalendarAlt className="me-2" />
                            Fecha Fin *
                          </Form.Label>
                          <Form.Control
                            type="date"
                            value={formData.fecha_fin}
                            onChange={(e) => setFormData({ ...formData, fecha_fin: e.target.value })}
                            min={formData.fecha_inicio}
                            className="dh-form-control"
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    {formData.fecha_inicio && formData.fecha_fin && (
                      <Alert 
                        variant={diasProgramacion % duracionCiclo === 0 && duracionCiclo > 0 ? 'success' : 'warning'} 
                        className={`dh-alert mb-3 ${diasProgramacion % duracionCiclo === 0 && duracionCiclo > 0 ? 'dh-alert-success' : 'dh-alert-warning'}`}
                        style={{ fontSize: '0.875rem' }}
                      >
                        <strong>Rango de días:</strong> {diasProgramacion} días
                        {duracionCiclo > 0 && (
                          <>
                            <br />
                            <strong>Ciclos completos:</strong> {ciclosCompletos}
                            {diasProgramacion % duracionCiclo !== 0 && (
                              <>
                                <br />
                                <small className="text-danger" style={{ fontSize: '0.8rem' }}>
                                  ⚠️ El rango debe ser múltiplo de {duracionCiclo} días
                                </small>
                              </>
                            )}
                          </>
                        )}
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      className="btn-dh-primary d-flex align-items-center"
                      disabled={loading || (duracionCiclo > 0 && diasProgramacion % duracionCiclo !== 0)}
                    >
                      <FaPlus className="me-2" />
                      {loading ? 'Creando...' : 'Crear Programación'}
                    </Button>
                  </Form>
                  </div>
                </Card.Body>
              </Card>

              <Card className="dh-card">
                <Card.Header className="dh-card-header dh-card-header-success">
                  <h6 className="mb-0" style={{ fontSize: '0.875rem', fontWeight: 700 }}>
                    <FaCalendarAlt className="me-2" />
                    Programaciones Existentes
                  </h6>
                </Card.Header>
                <Card.Body className="p-4">
                  {loading && <p style={{ fontSize: '0.875rem' }}>Cargando...</p>}
                  {!loading && programaciones.length === 0 && (
                    <Alert variant="info" className="dh-alert dh-alert-info" style={{ fontSize: '0.875rem' }}>
                      No hay programaciones creadas
                    </Alert>
                  )}
                  {!loading && programaciones.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                      <Table className="dh-table" size="sm">
                        <thead>
                          <tr>
                            <th>Nombre</th>
                            <th>Menú Cíclico</th>
                            <th>Fechas</th>
                            <th>Detalles</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {programaciones.map((prog) => (
                            <tr key={prog.id}>
                              <td style={{ fontSize: '0.75rem' }}>{prog.nombre}</td>
                              <td style={{ fontSize: '0.75rem' }}>{prog.menu_ciclico_nombre || 'N/A'}</td>
                              <td style={{ fontSize: '0.75rem' }}>
                                {formatDate(prog.fecha_inicio)} - {formatDate(prog.fecha_fin)}
                              </td>
                              <td>
                                <Badge bg="info" style={{ fontSize: '0.7rem' }}>{prog.total_detalles || 0}</Badge>
                              </td>
                              <td>
                                <Badge bg={prog.activo ? 'success' : 'secondary'} style={{ fontSize: '0.7rem' }}>
                                  {prog.activo ? 'Activo' : 'Inactivo'}
                                </Badge>
                              </td>
                              <td>
                                <Button
                                  className="btn-dh-danger"
                                  onClick={() => handleDelete(prog)}
                                  style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}
                                >
                                  <FaTrash />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Modal de confirmación de eliminación */}
        <ModalConfirmacion
          show={showConfirmModal}
          onHide={() => {
            setShowConfirmModal(false);
            setProgramacionToDelete(null);
          }}
          onConfirm={confirmDeleteProgramacion}
          title="Confirmar Eliminación"
          message={
            programacionToDelete
              ? `¿Está seguro de eliminar la programación "${programacionToDelete.nombre}"?`
              : ""
          }
          confirmText="Eliminar"
          cancelText="Cancelar"
          loading={loading}
          variant="danger"
        />
      </div>
    </>
  );
};

export default ProgramacionMenuCiclico;

